/**
 * Akta osobowe: części A–E (Etap 40, ADR-0041).
 *
 * § 3 rozporządzenia w sprawie dokumentacji pracowniczej dzieli akta osobowe
 * na pięć części:
 *
 *  - **A** — oświadczenia i dokumenty zebrane przy ubieganiu się o zatrudnienie
 *    oraz skierowania i orzeczenia lekarskie (wstępne, okresowe i kontrolne),
 *  - **B** — nawiązanie stosunku pracy i jego PRZEBIEG: umowa, zakres
 *    obowiązków, potwierdzenia zapoznania się z regulaminem i zasadami BHP,
 *    szkolenia, urlopy, praca zdalna,
 *  - **C** — rozwiązanie albo wygaśnięcie stosunku pracy, w tym świadectwo
 *    pracy i dokumenty o ekwiwalencie za urlop,
 *  - **D** — odpis zawiadomienia o ukaraniu i inne dokumenty odpowiedzialności
 *    porządkowej,
 *  - **E** — kontrola trzeźwości i kontrola na obecność środków działających
 *    podobnie do alkoholu.
 *
 * § 4 dodaje trzy reguły, które w systemie cyfrowym trzeba realizować wprost:
 * dokumenty przechowuje się w porządku CHRONOLOGICZNYM, NUMERUJE i opisuje
 * WYKAZEM dla każdej części; części D i E mają wydzielone podczęści na każdą
 * karę i każdą kontrolę (D1, D2, …), a po usunięciu dokumentu z podczęści
 * usuwa się ją całą i przenumerowuje pozostałe.
 *
 * Dlatego numeracja jest u nas WYPROWADZANA z porządku chronologicznego, a nie
 * zapisywana w kolumnie: po usunięciu dokumentu (na przykład po zatarciu kary
 * — art. 113 § 1 k.p.) przenumerowanie dzieje się samo i nie ma czego
 * uzgadniać.
 */
import type { DocumentType } from './enums'

export const PERSONNEL_FILE_PARTS = ['A', 'B', 'C', 'D', 'E'] as const
export type PersonnelFilePart = (typeof PERSONNEL_FILE_PARTS)[number]

/**
 * Domyślna część akt dla typu dokumentu. `null` znaczy: dokument NIE należy do
 * akt osobowych — dokumentację płacową i czasu pracy przechowuje się odrębnie
 * (art. 94 pkt 9a k.p.), a informacje podatkowe i ubezpieczeniowe wydaje się
 * pracownikowi.
 *
 * Kwalifikacja jest domyślna, nie ostateczna: kadry mogą przenieść dokument do
 * innej części, bo o przynależności decyduje TREŚĆ, a nie sam typ.
 */
export function defaultFilePart(type: DocumentType): PersonnelFilePart | null {
  switch (type) {
    /* Orzeczenia lekarskie — wstępne, okresowe i kontrolne — idą do części A. */
    case 'orzeczenie':
      return 'A'
    /*
     * Część B. Odpisy klauzuli RODO i informacji o warunkach to potwierdzenia
     * zapoznania (§ 3 rozporządzenia o dokumentacji pracowniczej). Przy umowie
     * cywilnej odpis klauzuli leży POZA aktami — akt osobowych nie prowadzi się
     * dla zleceniobiorcy (art. 94 pkt 9a k.p.); rozstrzyga to miejsce zapisu
     * odpisu, nie ta kwalifikacja domyślna.
     */
    case 'umowa':
    case 'aneks':
    case 'zaswiadczenie':
    case 'regulamin':
    case 'protokol_bhp':
    case 'inny':
    case 'klauzula_rodo':
    case 'informacja_o_warunkach':
      return 'B'
    case 'swiadectwo':
      return 'C'
    case 'zawiadomienie_o_karze':
      return 'D'
    /*
     * Poza aktami osobowymi: informacje podatkowe i ubezpieczeniowe (PIT-11,
     * ZUS IMIR, IFT-1R i IFT-1 dla nierezydenta), karta ewidencji czasu pracy,
     * oświadczenie o zajęciu wynagrodzenia oraz zgłoszenie umowy o dzieło na
     * formularzu RUD (Etap 47) — to dokumentacja płacowa, czasu pracy
     * i ubezpieczeniowa.
     *
     * IFT-1R wpadał tu do gałęzi domyślnej od Etapu 48, więc dokument założony
     * trasą modułu dokumentów lądował w części B; trasa informacji zawsze
     * podawała `null` wprost, więc wystawione informacje były poprawne, ale
     * kwalifikacja domyślna kłamała (Etap 52).
     */
    case 'pit11':
    case 'rmua':
    case 'ift1r':
    case 'ift1':
    case 'ewidencja_czasu_pracy':
    case 'zgloszenie_rud':
    case 'oswiadczenie_zajecie':
      return null
    default:
      return 'B'
  }
}

export function isPersonnelFilePart(value: unknown): value is PersonnelFilePart {
  return typeof value === 'string' && PERSONNEL_FILE_PARTS.includes(value as PersonnelFilePart)
}

/**
 * Czy część dzieli się na podczęści na każdą sprawę (§ 4): D na każdą karę,
 * E na każdą kontrolę. W A, B i C podczęści są możliwe, ale nieobowiązkowe —
 * my ich nie tworzymy, bo nie mamy z czego (dokumenty nie są tematycznie
 * powiązane w komplety).
 */
export function hasCaseSubparts(part: PersonnelFilePart): boolean {
  return part === 'D' || part === 'E'
}
